import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MatCardModule } from "@angular/material/card";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSnackBar, MatSnackBarConfig } from "@angular/material/snack-bar";
import { Guid } from "guid-typescript";
import { CreateExpenditureReport, ExpenditureItem, ExpenditureReport, Project, UpdateExpenditureReport } from "../../../../../common/models/project";
import { CostType } from "../../../../../common/models/common";
import { ProjectService } from "../../../../../common/services/project.service";
import { FileUploadComponent } from "../../../../../common/components/file/file.component";
import { ActivatedRoute, Router } from "@angular/router";
import { MatExpansionModule, MatExpansionPanel } from "@angular/material/expansion";
import { FormattedNumberInputDirective } from "../../../../../common/directives/inputs/formatted-number.directive";
import { ProjectBudgetStatus } from "../../../../../common/enums/project-budget-status";
import { Subject, takeUntil } from "rxjs";


@Component({
    selector: 'app-expenditure',
    templateUrl: './expenditure.component.html',
    styleUrls: ['./expenditure.component.scss'],
    imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatDatepickerModule,
    MatTooltipModule,
    MatExpansionModule,
    FileUploadComponent,
    FormattedNumberInputDirective
]
})
export class ExpenditureComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    @ViewChildren('directPanel', { read: MatExpansionPanel }) directExpansionPanels!: QueryList<MatExpansionPanel>;
    @ViewChildren('directDescriptionInputs') directDescriptionInputs!: QueryList<ElementRef<HTMLTextAreaElement>>;

    @ViewChildren('indirectPanel', { read: MatExpansionPanel }) indirectExpansionPanels!: QueryList<MatExpansionPanel>;

    @ViewChildren('indirectDescriptionInputs') indirectDescriptionInputs!: QueryList<ElementRef<HTMLTextAreaElement>>;


    expenditureReport: ExpenditureReport | null = null;
    projectId!: Guid;
    reportId!: Guid;

    expenditureReportForm: FormGroup;
    costType = CostType;
    costTypeOptions = Object.entries({
        [CostType.Direct]: 'Izravni trošak',
        [CostType.Indirect]: 'Neizravni trošak'
    }).map(([key, value]) => ({ key: +key, value }));

    private readonly snackbarConfig: MatSnackBarConfig = {
        duration: 3000,
        horizontalPosition: 'right',
        verticalPosition: 'top'
    };
    


    get directCosts(): FormArray {
        return this.expenditureReportForm.get('directCosts') as FormArray;
    }

    get indirectCosts(): FormArray {
        return this.expenditureReportForm.get('indirectCosts') as FormArray;
    }

    // Calculated Totals
    get totalDirectContractedFunds(): number {
        return this.directCosts.controls.reduce((sum, ctrl) => {
            return sum + (ctrl.get('contractedFundsAmount')?.value || 0);
        }, 0);
    }

    get totalDirectRealizedCosts(): number {
        return this.directCosts.controls.reduce((sum, ctrl) => {
            return sum + (ctrl.get('realizedEligibleCosts')?.value || 0);
        }, 0);
    }

    get totalIndirectContractedFunds(): number {
        return this.indirectCosts.controls.reduce((sum, ctrl) => {
            return sum + (ctrl.get('contractedFundsAmount')?.value || 0);
        }, 0);
    }

    get totalIndirectRealizedCosts(): number {
        return this.indirectCosts.controls.reduce((sum, ctrl) => {
            return sum + (ctrl.get('realizedEligibleCosts')?.value || 0);
        }, 0);
    }

    get totalContractedFunds(): number {
        return this.totalDirectContractedFunds + this.totalIndirectContractedFunds;
    }

    get totalRealizedEligibleCosts(): number {
        return this.totalDirectRealizedCosts + this.totalIndirectRealizedCosts;
    }

    get totalExpenditure(): number {
        return this.totalRealizedEligibleCosts;
    }

    constructor(
        private route: ActivatedRoute,        
        private router: Router,
        private fb: FormBuilder,
        private snackBar: MatSnackBar,
        private projectService: ProjectService
    ) {
        this.expenditureReportForm = this.fb.group({
            id: [''],
            reportingPeriodStart: [null, Validators.required],
            reportingPeriodEnd: [null, Validators.required],
            directCosts: this.fb.array([]),
            indirectCosts: this.fb.array([])
        });
    }

    ngOnInit(): void {
        // Extract projectId and reportId from route parameters
        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const projectIdParam = params.get('projectId');
            const reportIdParam = params.get('reportId');

            // Validate and assign projectId
            if (projectIdParam && Guid.isGuid(projectIdParam)) {
                this.projectId = Guid.parse(projectIdParam);

                // Check if reportId is present for editing an existing report
                if (reportIdParam && Guid.isGuid(reportIdParam)) {
                    this.reportId = Guid.parse(reportIdParam);
                    this.loadExpenditureReport();
                } else {
                    // If no reportId, assume creation of a new report
                    this.preloadExpenditureItemsFromLatestApprovedProjectBudget();
                }
            } else {
                // Handle missing or invalid projectId
                console.error('Invalid or missing project id.');
                this.router.navigate(['association', 'projects']);
            }
        });
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }

     preloadExpenditureItemsFromLatestApprovedProjectBudget(): void {
        // Load project budgets to get the budget plan
        this.projectService.getProjectBudgets(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (projectBudgets) => {
                // Preload expenditure items from the budget plan
                if (projectBudgets && projectBudgets.length > 0) {

                    // Find the latest approved ProjectBudget
                    const approvedProjectBudgets = projectBudgets.filter(pb => pb.status === ProjectBudgetStatus.Approved);
                    
                    if (approvedProjectBudgets.length === 0) {
                        console.error('No approved project budget found, no expenditure items preloaded.');
                        return;
                    }

                    // Find the ProjectBudget with the maximum budgetVersion
                    const latestApprovedProjectBudget = approvedProjectBudgets.reduce((prev, current) => {
                      return (prev.budgetVersion > current.budgetVersion) ? prev : current;
                    });

                    const projectBudgetItems = latestApprovedProjectBudget.projectBudgetItems || [];

                    this.directCosts.clear();
                    this.indirectCosts.clear();

                    // For each budget item, create a corresponding expenditure item
                    projectBudgetItems.forEach(pbi => {
                        const item: ExpenditureItem = {
                            id: Guid.parse(Guid.EMPTY),
                            projectBudgetItemId: pbi.id,
                            type: pbi.type,
                            description: pbi.description,
                            contractedFundsAmount: pbi.amountRequested,
                            realizedEligibleCosts: 0,
                            fileStorageLocation: `expenditureitems/${Guid.create().toString()}`
                        };
                        this.addExpenditureItem(item.type, item, false);
                    });
                } else {
                    console.error('No budget plan found, no items preloaded.');
                }
            },
            error: (err) => {
                console.error('Error fetching project budgets for preload', err);
            }
        });
    }


     loadExpenditureReport(): void {
        if (this.projectId && this.reportId) {
            this.projectService.getExpenditureReport(this.projectId, this.reportId).pipe(takeUntil(this.destroy$)).subscribe({
                next: (report) => {
                    this.expenditureReport = report;
                    if (this.expenditureReport) {
                        this.populateExpenditureReport(this.expenditureReport);
                    } else {
                        console.error('Expenditure report not found.');
                        this.router.navigate(['association', 'projects', this.projectId, 'reports']);
                    }
                },
                error: (err) => {
                    console.error('Error fetching expenditure report', err);
                    this.router.navigate(['association', 'projects']);
                }
            });
        }
    }

    addExpenditureItem(type: CostType, item?: ExpenditureItem, autoFocus: boolean = true): void {
        const expenditureItemForm = this.fb.group({
            id: [item?.id || ''],
            projectBudgetItemId: [item?.projectBudgetItemId || ''],
            type: [type, Validators.required],
            description: [item?.description || '', [Validators.required, Validators.maxLength(500)]],
            contractedFundsAmount: [item?.contractedFundsAmount || 0, [Validators.required, Validators.min(0)]],
            realizedEligibleCosts: [item?.realizedEligibleCosts || 0, [Validators.required, Validators.min(0)]],
            fileStorageLocation: [item?.fileStorageLocation || `expenditureitems/${Guid.create().toString()}`]
        });


        if (type === CostType.Direct) {
            this.directCosts.push(expenditureItemForm);
        } else {
            this.indirectCosts.push(expenditureItemForm);
        }
    
        // Run after view updates so QueryLists are correct
        setTimeout(() => {
            if (autoFocus) {
                if (type === CostType.Direct && this.directExpansionPanels.length > 0) {
                    const panel = this.directExpansionPanels.toArray()[this.directExpansionPanels.length - 1];
                    panel?.open();

                    setTimeout(() => {
                        const lastInput = this.directDescriptionInputs.last?.nativeElement;
                        lastInput?.focus();
                    }, 50);
 
                    
                } else if (type === CostType.Indirect && this.indirectExpansionPanels.length > 0) {
                    const panel = this.indirectExpansionPanels.toArray()[this.indirectExpansionPanels.length - 1];
                    panel?.open();

                    setTimeout(() => {
                        const lastInput = this.indirectDescriptionInputs.last?.nativeElement;
                        lastInput?.focus();
                    }, 50);
                }
            }
        }, 50);
    }

    removeExpenditureItem(type: CostType, index: number): void {
        if (type === CostType.Direct) {
            this.directCosts.removeAt(index);
        } else {
            this.indirectCosts.removeAt(index);
        }
        this.snackBar.open('Stavka troška uspješno obrisana', 'Zatvori', this.snackbarConfig);
    }

      closePanel(panel: MatExpansionPanel): void {
        panel.close();
      }

    populateExpenditureReport(report: ExpenditureReport): void {
        this.expenditureReportForm.patchValue({
            id: report.id.toString(),
            reportingPeriodStart: report.reportingPeriodStart,
            reportingPeriodEnd: report.reportingPeriodEnd
        });

        this.directCosts.clear();
        this.indirectCosts.clear();

        report.expenditureItems.forEach(item => {
            this.addExpenditureItem(item.type, item, false);
        });
    }

    onSubmit(): void {
        if (this.expenditureReportForm.invalid) {
            return;
        }

        if (this.expenditureReport) {
            this.updateExpenditureReport();
        } else {
            this.createExpenditureReport();
        }
    }

    createExpenditureReport(): void {
        if (!this.projectId) {
            console.log('Project id is missing in createExpenditureReport.');
            return;
        }

        const createDto: CreateExpenditureReport = {
            reportingPeriodStart: this.expenditureReportForm.get('reportingPeriodStart')?.value,
            reportingPeriodEnd: this.expenditureReportForm.get('reportingPeriodEnd')?.value,
            expenditureItems: [
                ...this.directCosts.controls.map(ctrl => ({
                    ...ctrl.value,
                    id: Guid.EMPTY,
                    projectBudgetItemId: ctrl.value.projectBudgetItemId ? Guid.parse(ctrl.value.projectBudgetItemId).toString() : null,
                })),
                ...this.indirectCosts.controls.map(ctrl => ({
                    ...ctrl.value,
                    id: Guid.EMPTY,
                    projectBudgetItemId: ctrl.value.projectBudgetItemId ? Guid.parse(ctrl.value.projectBudgetItemId).toString() : null,
                }))
            ]
        };

        this.projectService.createExpenditureReport(this.projectId, createDto).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.snackBar.open('Financijski izvještaj uspješno kreiran.', 'Zatvori', this.snackbarConfig);
                this.router.navigate(['association', 'projects', this.projectId.toString(), 'reports']);
            },
            error: (error) => {
                this.snackBar.open('Greška prilikom kreiranja financijskog izvještaja', 'Zatvori', this.snackbarConfig);
                console.error(error);
            }
        });
    }

    updateExpenditureReport(): void {
        if (!this.expenditureReport){
            return;
        }

        const updateDto: UpdateExpenditureReport = {
            reportingPeriodStart: this.expenditureReportForm.get('reportingPeriodStart')?.value,
            reportingPeriodEnd: this.expenditureReportForm.get('reportingPeriodEnd')?.value,
            expenditureItems: [
                ...this.directCosts.controls.map(ctrl => ({
                    ...ctrl.value,
                    id: ctrl.value.id ? Guid.parse(ctrl.value.id).toString() : Guid.EMPTY,
                    projectBudgetItemId: ctrl.value.projectBudgetItemId ? Guid.parse(ctrl.value.projectBudgetItemId).toString() : null,
                })),
                ...this.indirectCosts.controls.map(ctrl => ({
                    ...ctrl.value,
                    id: ctrl.value.id ? Guid.parse(ctrl.value.id).toString() : Guid.EMPTY,
                    projectBudgetItemId: ctrl.value.projectBudgetItemId ? Guid.parse(ctrl.value.projectBudgetItemId).toString() : null,
                }))
            ]
        };

        this.projectService.updateExpenditureReport(this.projectId, this.expenditureReport.id, updateDto).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.snackBar.open('Financijski izvještaj uspješno ažuriran', 'Zatvori', this.snackbarConfig);
                this.router.navigate(['association', 'projects', this.projectId.toString(), 'reports']);
            },
            error: (error) => {
                this.snackBar.open('Greška prilikom ažuriranja financijskog izvještaja', 'Zatvori', this.snackbarConfig);
                console.error(error);
            }
        });
    }

}
